
import React, { useState, useEffect, useRef } from 'react';
import { X, Smartphone, ArrowRight, CheckCircle2, ShieldCheck, RefreshCcw, AlertTriangle, Ban } from 'lucide-react';
import { ToastType } from './ToastNotification';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
  showToast?: (message: string, type: ToastType) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLoginSuccess, showToast }) => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [errorStatus, setErrorStatus] = useState<'none' | 'banned' | 'spam'>('none');
  
  const submittingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [timer, setTimer] = useState(180);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setStep('phone');
      setPhoneNumber('');
      setOtp('');
      setTimer(180);
      setErrorStatus('none');
      submittingRef.current = false;
    } else {
      setTimeout(() => setIsVisible(false), 300);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    let interval: any;
    if (step === 'otp' && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handleErrorResponse = (status: number, data: any) => {
    const message = data.detail || data.message || '';
    
    if (status === 403) {
      if (message.includes('مسدود') || (typeof message === 'object' && message.type === 'BANNED')) {
        setErrorStatus('banned');
        if (showToast) showToast('حساب کاربری شما مسدود شده است', 'error');
      } else if (message.includes('تلاش بیش از حد') || message.includes('درخواست بیش از حد')) {
        setErrorStatus('spam');
        if (showToast) showToast('تعداد درخواست بیش از حد مجاز؛ لطفاً بعداً تلاش کنید', 'error');
      } else {
        if (showToast) showToast(typeof message === 'string' ? message : 'دسترسی محدود شده است', 'error');
      }
    } else {
      if (showToast) showToast(typeof message === 'string' ? message : 'مشکلی در برقراری ارتباط وجود دارد', 'error');
    }
  };

  const verifyOtpLogic = async () => {
    if (submittingRef.current) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    submittingRef.current = true;
    setIsLoading(true);
    setErrorStatus('none');

    try {
      const response = await fetch('https://api.f4st.ir/api/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          mobile: phoneNumber,
          code: otp 
        }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (response.ok) {
        onLoginSuccess();
      } else {
        handleErrorResponse(response.status, data);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        if (showToast) showToast('مشکلی در تایید کد وجود دارد', 'error');
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setIsLoading(false);
        submittingRef.current = false;
        abortControllerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (step === 'otp' && otp.length === 6) {
      verifyOtpLogic();
    }
  }, [otp]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorStatus('none');
    
    if (!phoneNumber.startsWith('09') || phoneNumber.length !== 11) {
      if (showToast) showToast('لطفاً شماره موبایل معتبر (با 09) وارد کنید', 'error');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('https://api.f4st.ir/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ mobile: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep('otp');
        setTimer(180);
        if (showToast) showToast('کد تایید ارسال شد', 'success');
      } else {
        handleErrorResponse(response.status, data);
      }
    } catch (error: any) {
      console.error(error);
      if (showToast) showToast('مشکلی پیش آمده است، لطفا مجدد تلاش کنید', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      verifyOtpLogic();
    }
  };

  const handleResendCode = async () => {
    if (timer > 0 || isLoading) return;
    
    setIsLoading(true);
    setErrorStatus('none');
    try {
       const response = await fetch('https://api.f4st.ir/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ mobile: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok) {
        setTimer(180);
        if (showToast) showToast('کد تایید مجدداً ارسال شد', 'success');
      } else {
         handleErrorResponse(response.status, data);
      }
    } catch (error: any) {
      if (showToast) showToast('خطا در ارسال مجدد کد', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible && !isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="absolute inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

      <div className={`relative bg-white dark:bg-slate-900 w-full max-w-md mx-4 rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'}`}>
        
        <div className={`h-32 transition-colors duration-500 ${errorStatus === 'banned' ? 'bg-rose-600' : errorStatus === 'spam' ? 'bg-amber-500' : 'bg-gradient-to-br from-violet-600 to-blue-600'} relative overflow-hidden flex items-center justify-center`}>
          <div className="absolute inset-0 opacity-20">
             <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
               <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
             </svg>
          </div>
          <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-md shadow-lg border border-white/30 relative z-10 animate-bounce-slow">
            {errorStatus === 'banned' ? <Ban className="text-white w-10 h-10" /> : errorStatus === 'spam' ? <AlertTriangle className="text-white w-10 h-10" /> : <ShieldCheck className="text-white w-10 h-10" />}
          </div>
          <button onClick={onClose} className="absolute top-4 left-4 text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-8">
          {errorStatus === 'banned' ? (
            <div className="text-center animate-in fade-in zoom-in duration-300">
               <h2 className="text-2xl font-black text-rose-600 mb-4">حساب مسدود شده</h2>
               <p className="text-slate-600 dark:text-slate-400 leading-7 mb-8">
                 متأسفانه حساب کاربری شما به دلیل نقض قوانین سیستم مسدود شده است. در صورت اعتراض، از طریق تیکت یا ایمیل با ما در ارتباط باشید.
               </p>
               <button onClick={onClose} className="w-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 py-3 rounded-xl font-bold">بستن</button>
            </div>
          ) : (
            <div className="animate-in slide-in-from-right duration-300">
              {step === 'phone' ? (
                <>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-2">ورود</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">شماره موبایل خود را برای دریافت کد وارد کنید</p>
                  </div>

                  <form onSubmit={handlePhoneSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-300 mr-1 block">شماره موبایل</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-violet-600 dark:group-focus-within:text-violet-400 transition-colors">
                          <Smartphone size={20} />
                        </div>
                        <input
                          type="tel"
                          dir="ltr"
                          placeholder="09123456789"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                          className={`w-full pl-4 pr-12 py-4 rounded-xl border-2 ${errorStatus === 'spam' ? 'border-amber-400' : 'border-slate-200 dark:border-slate-700'} bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400 outline-none focus:border-violet-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-sans text-left text-lg tracking-widest font-bold`}
                          autoFocus
                          maxLength={11}
                        />
                      </div>
                      {errorStatus === 'spam' && <p className="text-xs text-amber-600 font-bold mt-1">تلاش بیش از حد؛ لطفاً بعداً دوباره امتحان کنید.</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full ${errorStatus === 'spam' ? 'bg-amber-500' : 'bg-gradient-to-r from-violet-600 to-blue-600'} text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:-translate-y-1 active:scale-95 disabled:opacity-70 transition-all flex items-center justify-center gap-2`}
                    >
                      {isLoading ? (
                        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>{errorStatus === 'spam' ? 'تلاش مجدد' : 'دریافت کد تایید'}</span>
                          <ArrowRight size={20} className="rotate-180" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-2">کد تایید</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">کد ارسال شده به شماره زیر را وارد کنید</p>
                    <div className="flex items-center justify-center gap-2 text-violet-600 dark:text-violet-400 font-bold dir-ltr bg-violet-50 dark:bg-violet-900/30 inline-block px-3 py-1 rounded-full text-sm mt-2 cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors" onClick={() => setStep('phone')}>
                      {phoneNumber}
                      <span className="text-[10px] text-slate-400 font-normal mr-1">(ویرایش)</span>
                    </div>
                  </div>

                  <form onSubmit={handleOtpSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="text"
                          dir="ltr"
                          placeholder="— — — — — —"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                          className="w-full py-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-300 dark:placeholder-slate-600 outline-none focus:border-violet-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-sans text-center text-3xl tracking-[0.5em] font-bold"
                          autoFocus
                          maxLength={6}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={otp.length !== 6 || isLoading}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:-translate-y-1 active:scale-95 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>ورود</span>
                          <CheckCircle2 size={20} />
                        </>
                      )}
                    </button>
                    
                    <div className="text-center">
                      {timer > 0 ? (
                        <span className="text-slate-500 dark:text-slate-400 text-sm font-bold tabular-nums">
                           {formatTime(timer)} تا ارسال مجدد
                        </span>
                      ) : (
                        <button 
                          type="button" 
                          onClick={handleResendCode}
                          className="text-violet-600 dark:text-violet-400 text-sm font-bold hover:underline flex items-center justify-center gap-1 mx-auto"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <div className="w-4 h-4 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin" />
                          ) : (
                            <RefreshCcw size={14} />
                          )}
                          ارسال مجدد کد
                        </button>
                      )}
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
